import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Form, FormField } from '../ui/form';
import Plus from 'lucide-react/dist/esm/icons/plus';
import Edit2 from 'lucide-react/dist/esm/icons/edit-2';
import Trash2 from 'lucide-react/dist/esm/icons/trash-2';
import Server from 'lucide-react/dist/esm/icons/server';
import Printer from 'lucide-react/dist/esm/icons/printer';
import Monitor from 'lucide-react/dist/esm/icons/monitor';
import Upload from 'lucide-react/dist/esm/icons/upload';
import Download from 'lucide-react/dist/esm/icons/download';
import FileText from 'lucide-react/dist/esm/icons/file-text';

interface Device {
  id: string;
  name: string;
  type: string;
  model: string;
  serial_number: string;
  purchase_date: string;
  warranty_info?: string;
  notes?: string;
  backup_status: string;
  last_backup?: string;
  file_path?: string;
  file_size?: number;
  file_type?: string;
}

export function Devices() {
  const { user } = useAuth();
  const [devices, setDevices] = useState<Device[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const deviceFields: FormField[] = [
    {
      name: 'name',
      label: 'Device Name',
      type: 'text',
      required: true,
      placeholder: 'Enter device name'
    },
    {
      name: 'type',
      label: 'Device Type',
      type: 'select',
      required: true,
      options: [
        { value: 'laptop', label: 'Laptop' },
        { value: 'desktop', label: 'Desktop' },
        { value: 'tablet', label: 'Tablet' },
        { value: 'smartphone', label: 'Smartphone' },
        { value: 'server', label: 'Server' },
        { value: 'printer', label: 'Printer' },
        { value: 'monitor', label: 'Monitor' },
        { value: 'external_storage', label: 'External Storage' },
        { value: 'other', label: 'Other' }
      ]
    },
    {
      name: 'model',
      label: 'Model',
      type: 'text',
      required: true,
      placeholder: 'Enter device model'
    },
    {
      name: 'serial_number',
      label: 'Serial Number',
      type: 'text',
      required: false,
      placeholder: 'Enter serial number'
    },
    {
      name: 'purchase_date',
      label: 'Purchase Date',
      type: 'date',
      required: false
    },
    {
      name: 'warranty_info',
      label: 'Warranty Information',
      type: 'text',
      placeholder: 'Enter warranty details'
    },
    {
      name: 'notes',
      label: 'Notes',
      type: 'textarea',
      placeholder: 'Enter any additional notes'
    }
  ];

  const fetchDevices = useCallback(async (background = false): Promise<void> => {
    try {
      if (!user) return;

      const { data, error } = await supabase
        .from('devices')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setDevices(data || []);
    } catch (err) {
      if (!background) setError(err instanceof Error ? err.message : 'Failed to fetch devices');
    }
  }, [user]);

  const handleDeleteDevice = async (deviceId: string) => {
    try {
      const { error } = await supabase
        .from('devices')
        .delete()
        .eq('id', deviceId);

      if (error) throw error;

      fetchDevices();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete device');
    }
  };

  const handleFileUpload = async (deviceId: string, file: File): Promise<void> => {
    try {
      setUploading(true);
      
      if (!user) throw new Error('User not authenticated');

      const fileExt = file.name.split('.').pop();
      const fileName = `${deviceId}/${Date.now()}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      // Upload file to storage
      const { error: uploadError } = await supabase.storage
        .from('digital-estate-files')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Update device record with file information
      const { error: updateError } = await supabase
        .from('devices')
        .update({
          file_path: filePath,
          file_size: file.size,
          file_type: file.type
        })
        .eq('id', deviceId);

      if (updateError) throw updateError;

      // Refresh devices list
      fetchDevices();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleFileDownload = async (device: Device): Promise<void> => {
    try {
      if (!device.file_path) return;

      const { data, error } = await supabase.storage
        .from('digital-estate-files')
        .download(device.file_path);

      if (error) throw error;

      // Create download link
      const url = window.URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = device.name + '_documentation';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download file');
    }
  };

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  useEffect(() => {
    const handler = () => { if (user) fetchDevices(true); };
    window.addEventListener('refresh-digital', handler);
    return () => window.removeEventListener('refresh-digital', handler);
  }, [user, fetchDevices]);

  const getDeviceIcon = (type: string): JSX.Element => {
    switch (type.toLowerCase()) {
      case 'laptop':
      case 'desktop':
        return <Monitor className="w-6 h-6" />;
      case 'server':
        return <Server className="w-6 h-6" />;
      case 'printer':
        return <Printer className="w-6 h-6" />;
      default:
        return <Server className="w-6 h-6" />;
    }
  };

  const getBackupStatusColor = (status: string): string => {
    switch (status) {
      case 'backed_up':
        return 'text-green-600 bg-green-100';
      case 'partially_backed_up':
        return 'text-yellow-600 bg-yellow-100';
      case 'not_backed_up':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Digital Devices</h2>
        <Button onClick={() => setShowAddForm(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Device
        </Button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {devices.map((device) => (
          <Card key={device.id} className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-4">
                {getDeviceIcon(device.type)}
                <div>
                  <h3 className="font-semibold">{device.name}</h3>
                  <p className="text-sm text-gray-500 capitalize">{device.type.replace('_', ' ')}</p>
                </div>
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedDevice(device);
                    setShowEditModal(true);
                  }}
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteDevice(device.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              <p className="text-sm">
                <span className="font-medium">Model:</span> {device.model}
              </p>
              <p className="text-sm">
                <span className="font-medium">Serial Number:</span> {device.serial_number}
              </p>
              <p className="text-sm">
                <span className="font-medium">Purchase Date:</span> {device.purchase_date}
              </p>
              {device.warranty_info && (
                <p className="text-sm">
                  <span className="font-medium">Warranty:</span> {device.warranty_info}
                </p>
              )}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Backup Status:</span>
                <span className={`text-xs px-2 py-1 rounded-full ${getBackupStatusColor(device.backup_status)}`}>
                  {device.backup_status.replace('_', ' ')}
                </span>
              </div>
              {device.notes && (
                <p className="text-sm">
                  <span className="font-medium">Notes:</span> {device.notes}
                </p>
              )}
            </div>
            
            {/* File upload/download section */}
            <div className="mt-4 pt-4 border-t">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Warranty</span>
                <div className="flex gap-2">
                  {device.file_path ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleFileDownload(device)}
                      icon={<Download className="w-4 h-4" />}
                    >
                      Download
                    </Button>
                  ) : (
                    <div className="relative">
                      <input
                        type="file"
                        id={`file-upload-${device.id}`}
                        className="hidden"
                        accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleFileUpload(device.id, file);
                          }
                        }}
                        disabled={uploading}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => document.getElementById(`file-upload-${device.id}`)?.click()}
                        icon={uploading ? <div className="animate-spin w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full" /> : <Upload className="w-4 h-4" />}
                        disabled={uploading}
                      >
                        {uploading ? 'Uploading...' : 'Upload'}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              {device.file_path && (
                <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                  <FileText className="w-3 h-3" />
                  <span>{device.file_type}</span>
                  <span>({(device.file_size || 0 / 1024 / 1024).toFixed(2)} MB)</span>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>

      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <Form
            title="Add New Device"
            fields={deviceFields}
            tableName="devices"
            submitLabel="Save Device"
            onSuccess={() => {
              setShowAddForm(false);
              fetchDevices();
            }}
            onCancel={() => setShowAddForm(false)}
          />
        </div>
      )}

      {showEditModal && selectedDevice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <Form
            title="Edit Device"
            fields={deviceFields}
            tableName="devices"
            submitLabel="Update Device"
            recordId={selectedDevice.id}
            onSuccess={() => {
              setShowEditModal(false);
              setSelectedDevice(null);
              fetchDevices();
            }}
            onCancel={() => {
              setShowEditModal(false);
              setSelectedDevice(null);
            }}
          />
        </div>
      )}
    </div>
  );
}